import React from 'react';
import '../../index.css';
import { useAuth } from '../../context/authContext';
import { Card, CardContent, CardHeader } from '../ui/card';
import PaymentStatus from './PaymentStatus';
import PaymentStatusJourney from './PaymentStatusJourney';
import { Badge } from '../ui/badge';
import { Building2, CreditCard, FileText, Layers } from 'lucide-react';
import { getPaymentBatchSummary } from '../../utils/paymentBatch';

const currencyFormat = (value) => {
    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const convertAccountDetails = (type) => {
    switch (type) {
        case 'IBAN':
            return 'IBAN';
        case 'BIC':
            return 'BIC';
        case 'ACCOUNT_NO':
            return 'Account Number';
        case 'ACCOUNT_NUMBER':
            return 'Account Number';
        case 'SORT_CODE':
            return 'Sort Code';
        case 'ROUTING_NUMBER':
            return 'Routing Number';
        case 'SWIFT_CODE':
            return 'SWIFT Code';
        case 'ROUTING_NO':
            return 'Routing Number';
        default:
            return '';
    }
};

const formatAccountDetails = (accounts = []) => {
    if (!accounts.length) return '—';
    return accounts
        .map((account) => `${convertAccountDetails(account.type)}: ${account.identification}`)
        .join('\n');
};

const SummaryField = ({ icon: Icon, label, value, className, multiline }) => (
    <div className={`flex items-start gap-3 py-3 ${className || ''}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
            <Icon className="h-4 w-4 text-slate-500" />
        </span>
        <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{label}</p>
            <div className={`text-sm font-medium text-[#2E2E2E] break-words ${multiline ? 'whitespace-pre-line' : ''}`}>{value}</div>
        </div>
    </div>
);

const PaymentSummary = ({ paymentDetails }) => {
    const { authenticated } = useAuth();
    const payment = paymentDetails;
    const { totalAmount, currency, isMultiCurrency, totalsByCurrency } = getPaymentBatchSummary(payment.payments);

    let statusNumber;
    if (payment.obtainedAuthorisations === 0) {
        statusNumber = 0;
    } else if (payment.obtainedAuthorisations === payment.requiredAuthorisations) {
        statusNumber = 2;
    } else {
        statusNumber = 1;
    }

    const isRejected = payment.status === 'Rejected';

    return (
        <div className="w-full h-full">
            <Card className="h-full flex flex-col overflow-hidden">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 border border-slate-200">
                                <FileText className="h-4 w-4 text-slate-500" />
                            </span>
                            <h3 className="my-0">Payment summary</h3>
                        </div>
                        <PaymentStatus status={payment.status} />
                    </div>
                </CardHeader>
                <CardContent className="flex-1 pt-4">
                    {authenticated && payment ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                                <SummaryField
                                    icon={CreditCard}
                                    label="Payment method"
                                    value={
                                        <span className="flex flex-wrap items-center gap-2">
                                            {payment.paymentMethod}
                                            {payment.intercompanyTransfer && (
                                                <Badge variant="info">Intercompany</Badge>
                                            )}
                                        </span>
                                    }
                                    className="sm:col-span-2"
                                />
                                <SummaryField
                                    icon={Building2}
                                    label="Payer"
                                    value={payment.payer.name}
                                />
                                <SummaryField
                                    icon={Building2}
                                    label="Account details"
                                    value={formatAccountDetails(payment.payer.accountIdentifications)}
                                    multiline
                                />
                                <SummaryField
                                    icon={CreditCard}
                                    label={isMultiCurrency ? 'Totals by currency' : 'Total value'}
                                    value={isMultiCurrency ? (
                                        <ul className="m-0 list-none space-y-1 p-0">
                                            {totalsByCurrency.map((total) => (
                                                <li key={total.currency}>
                                                    {currencyFormat(total.totalAmount)} {total.currency}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : `${currencyFormat(totalAmount)} ${currency}`}
                                />
                                <SummaryField
                                    icon={Layers}
                                    label="Total lines"
                                    value={payment.totalLines ?? payment.payments.length}
                                />
                            </div>

                            <div className="mt-6 pt-6 border-t border-slate-100">
                                <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-4">
                                    Authorisation progress
                                </p>
                                <div className="w-full">
                                    <PaymentStatusJourney
                                        status={statusNumber}
                                        isRejected={isRejected}
                                        obtainedAuthorisations={paymentDetails.obtainedAuthorisations}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="blur select-none">
                            <p>Details cannot be displayed.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default PaymentSummary;
